// Section 22 Jobs tab and Section 8: a board of job columns with counts and eligibility.
// Assign by click (select a person, then "Assign here"), by drag and drop, or by keyboard (Enter
// on a person, Tab to a column, Enter on "Assign here"). Each column shows projected output for
// the next four seasons (dashed: with no changes this season) and, for the selected person, the
// dip their move would cause (the bullwhip guard-rail). Vacant posts are listed.
import type { JobId, PersonId } from '../../sim/api';
import type { JobColumnVM, JobsVM, NeedLine, PersonRow } from '../../viewModels';
import { humanize, oneDecimal, seasonName, times, whole } from '../format';
import { resourceLabel, uiText } from '../text/en';
import { Sparkline } from '../charts/sparkline';
import { Glyphs } from '../shell/glyphs';

interface Props {
  vm: JobsVM;
  hovered: PersonId | null;
  canEdit: boolean;
  onSelect: (id: PersonId | null) => void;
  onAssign: (personId: PersonId, job: JobId) => void;
  onHover: (id: PersonId | null) => void;
}

const DRAG_TYPE = 'text/plain';

function outputLabel(c: JobColumnVM): string {
  if (c.output === null) return '';
  const label = resourceLabel[c.output as keyof typeof resourceLabel] as string | undefined;
  if (label === undefined) return `${humanize(c.output)} (${uiText.jobs.service})`;
  return label;
}

function Needs({ needs }: { needs: NeedLine[] }) {
  const t = uiText.jobs;
  return (
    <section class="jobs__needs" aria-labelledby="needs-title">
      <h3 id="needs-title">{t.needs}</h3>
      <ul>
        {needs.map((n) => (
          <li key={n.resource}>
            {t.needLine(resourceLabel[n.resource], whole(n.needed), whole(n.projected), whole(n.onHand))}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Chip({
  p,
  selected,
  hovered,
  canEdit,
  onSelect,
  onHover,
}: {
  p: PersonRow;
  selected: boolean;
  hovered: boolean;
  canEdit: boolean;
  onSelect: (id: PersonId | null) => void;
  onHover: (id: PersonId | null) => void;
}) {
  return (
    <li>
      <button
        type="button"
        class={`chip${selected ? ' is-selected' : ''}${p.pending ? ' is-pending' : ''}${hovered ? ' is-hovered' : ''}`}
        aria-pressed={selected}
        draggable={canEdit}
        onDragStart={(e) => {
          e.dataTransfer?.setData(DRAG_TYPE, p.id);
          onSelect(p.id);
        }}
        onClick={() => onSelect(selected ? null : p.id)}
        onMouseEnter={() => onHover(p.id)}
        onMouseLeave={() => onHover(null)}
        onFocus={() => onHover(p.id)}
        onBlur={() => onHover(null)}
      >
        <span class="name">{p.name}</span>
        <Glyphs flags={p.flags} />
        <span class="num">{oneDecimal(p.skill)}</span>
      </button>
    </li>
  );
}

function Column({ c, vm, ...props }: { c: JobColumnVM; vm: JobsVM } & Omit<Props, 'vm'>) {
  const t = uiText.jobs;
  const selected = vm.selected;
  const horizon = vm.horizon.map((h) => `${seasonName(h.season)} ${h.year}`);
  const seriesLabel =
    c.series.length === 0
      ? ''
      : `${t.projected}: ${c.series.map((v, i) => `${horizon[i] ?? ''} ${oneDecimal(v)}`).join(', ')}`;
  return (
    <section
      class={`jobs__column${selected !== null && c.selectedEligible === false ? ' is-blocked' : ''}`}
      aria-label={c.label}
      onDragOver={(e) => {
        if (props.canEdit) e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        const id = e.dataTransfer?.getData(DRAG_TYPE);
        if (id !== undefined && id !== '') props.onAssign(id as PersonId, c.id);
      }}
    >
      <header class="jobs__head">
        <h3>
          {c.label} <span class="num">{c.count}</span>
        </h3>
        <p class="hint">
          {t.eligible(c.eligible)}
          {c.heavy && ` · ${t.heavy}`}
          {c.requiresLabel !== null && ` · ${t.needsBuilding(c.requiresLabel.toLowerCase())}`}
        </p>
      </header>
      {c.output !== null && (
        <div class="jobs__output">
          {c.series.length > 0 ? (
            <Sparkline values={c.series} baseline={c.baseline} label={seriesLabel} />
          ) : null}
          <span class="hint">{outputLabel(c)}</span>
        </div>
      )}
      {c.vacancies.map((v) => (
        <p key={v.personId} class="jobs__vacant">
          <span aria-hidden="true">▢ </span>
          {t.vacant(v.name, t.vacantReason[v.reason] ?? v.reason)}
        </p>
      ))}
      {selected !== null && c.dip !== null && (
        <p class="jobs__dip">
          {c.dip.now === null
            ? t.dipFromRest(times(c.dip.moved), oneDecimal(c.dip.skillMoved))
            : t.dip(times(c.dip.now), times(c.dip.moved), oneDecimal(c.dip.skillNow), oneDecimal(c.dip.skillMoved))}
        </p>
      )}
      {selected !== null && c.selectedProblem !== null && <p class="hint">{c.selectedProblem}</p>}
      <button
        type="button"
        class="button button--small jobs__assign"
        disabled={!props.canEdit || selected === null || c.selectedEligible !== true || selected.job === c.id}
        aria-label={selected === null ? t.assignHere : t.assignTo(selected.name, c.label)}
        onClick={() => {
          if (selected !== null) props.onAssign(selected.id, c.id);
        }}
      >
        {t.assignHere}
      </button>
      <ul class="jobs__people">
        {c.people.map((p) => (
          <Chip
            key={p.id}
            p={p}
            selected={selected?.id === p.id}
            hovered={props.hovered === p.id}
            canEdit={props.canEdit}
            onSelect={props.onSelect}
            onHover={props.onHover}
          />
        ))}
      </ul>
    </section>
  );
}

export function JobsBoard(props: Props) {
  const { vm, ...rest } = props;
  const t = uiText.jobs;
  return (
    <div class={`jobs${vm.pending ? ' is-pending' : ''}`}>
      <p class="hint" role="status">
        {vm.selected === null ? t.hintNone : t.hintSelected(vm.selected.name)}
        {vm.selectedLosesPlot && <strong class="jobs__warn"> {t.losesPlot}</strong>}
      </p>
      <Needs needs={vm.needs} />
      <p class="hint">
        {t.projected} · {t.baseline}
      </p>
      <div class="jobs__board">
        {vm.columns.map((c) => (
          <Column key={c.id} c={c} vm={vm} {...rest} />
        ))}
      </div>
    </div>
  );
}
